import React from "react";
import { ContextMenuItem } from "../../types/system/ContextMenuItem";
import menuData from "../../resources/contextmenuitems";
import KeyMenuItem from "../../types/system/KeyMenuItem";
import Configuration from "../../foundation/Configuration";
import ShadowStaticRange from "../../types/system/ShadowStaticRange";
import Logger from "../../foundation/Logger";

interface ContextMenuContextProps {
    children?: any;

    refreshRoute: () => void;
}

interface ContextMenuContextState {
    contextTarget: HTMLElement;
    contextSelection?: ShadowStaticRange;
    contextValue: string | null;
    contextKey: string;
    menuItems: ContextMenuItem[];
    additionalMenuItems: ContextMenuItem[];
    keyMenuItems: KeyMenuItem[];
}

export interface ContextMenuContextType {
    menuItems: ContextMenuItem[];
    additionalMenuItems: ContextMenuItem[];
    addMenuItems: (items: ContextMenuItem[]) => void;
    setContext: (
        contextTarget: HTMLElement,
        contextSelection: ShadowStaticRange | undefined,
        contextValue: string,
        contextKey: string,
    ) => void;
    refreshRoute: () => void;
    getKey: () => string;
    getValue: () => string | null;
    hasTextSelection: () => boolean;
    isEditable: () => boolean;
    getContextTarget: () => HTMLElement;
    getContextSelection: () => ShadowStaticRange | undefined;
}

export const ContextMenuContext = React.createContext({
    menuItems: [] as ContextMenuItem[],
    additionalMenuItems: [] as ContextMenuItem[],
    addMenuItems: (items: ContextMenuItem[]) => {},
    setContext: (
        contextTarget: HTMLElement,
        contextSelection: ShadowStaticRange | undefined,
        contextValue: string,
        contextKey: string,
    ) => {},
    refreshRoute: () => {},

    getKey: () => "",
    getValue: () => "",
    hasTextSelection: () => false,
    isEditable: () => false,
    getContextTarget: () => null,
    getContextSelection: () => undefined,
} as ContextMenuContextType);

class ContextMenuContextContainer extends React.Component<
    ContextMenuContextProps,
    ContextMenuContextState
> {
    state: ContextMenuContextState = {
        contextTarget: null,
        contextValue: null,
        contextKey: "",
        menuItems: [],
        additionalMenuItems: [],
        keyMenuItems: [] as KeyMenuItem[],
    };

    static additionalMenuItems: ContextMenuItem[] = [];

    context: ContextMenuContextType;

    constructor(props: ContextMenuContextProps) {
        super(props);

        this.state.keyMenuItems = this.buildKeyMenus(
            this.state.menuItems.concat(this.state.additionalMenuItems),
        );
    }

    componentDidMount(): void {
        document.body.addEventListener("keydown", this.contextKeyDownHandler);
        document.body.addEventListener("keyup", this.contextKeyUpHandler);

        this.initMenuItems(menuData);
    }
    componentWillUnmount(): void {
        document.body.removeEventListener(
            "keydown",
            this.contextKeyDownHandler,
        );
        document.body.removeEventListener("keyup", this.contextKeyUpHandler);
    }

    buildKeyMenus(items: ContextMenuItem[]): KeyMenuItem[] {
        const keyMenu: KeyMenuItem[] = [];
        items.forEach(item => {
            if (item.shortcut && item.shortcut.length) {
                const newItem = new KeyMenuItem();

                newItem.keys = item.shortcut.split("+");

                const processModifier = type => {
                    const index = newItem.keys.indexOf(type);

                    if (index >= 0) newItem.keys.splice(index, 1);

                    return index >= 0;
                };

                newItem.hasCtrl =
                    processModifier("Ctrl") || processModifier("⌘");
                newItem.hasAlt = processModifier("Alt");
                newItem.hasShift = processModifier("Shift");

                newItem.contextMenuItem = item;

                keyMenu.push(newItem);
            }
        });
        return keyMenu;
    }

    contextKeyDown(event: React.KeyboardEvent) {
        const keys = [];
        switch (event.key) {
            case "Ctrl":
            case "Alt":
            case "Shift":
            case "Meta":
            case "Space":
            case "Enter":
            case "Return":
                return;
            default:
                keys.push(event.key.toUpperCase());
                break;
        }

        const matched = this.state.keyMenuItems.find(item => {
            return (
                (event.ctrlKey || event.metaKey) === item.hasCtrl &&
                event.altKey === item.hasAlt &&
                event.shiftKey === item.hasShift &&
                item.keys &&
                item.keys.filter(key => keys.indexOf(key) >= 0).length ===
                    item.keys.length
            );
        });

        if (matched?.contextMenuItem?.handler) {
            if (!matched.contextMenuItem.default) {
                event.preventDefault();
                if (!matched?.contextMenuItem?.disabled)
                    matched.contextMenuItem.handler(event, this.context);
            }
        }
    }
    contextKeyDownHandler = this.contextKeyDown.bind(this);
    contextKeyUp(event: KeyboardEvent) {}
    contextKeyUpHandler = this.contextKeyUp.bind(this);

    setContext(
        contextTarget: HTMLElement,
        contextSelection: ShadowStaticRange | undefined,
        contextValue: string,
        contextKey: string,
    ) {
        this.setState({
            contextTarget,
            contextSelection,
            contextValue,
            contextKey,
        });
    }
    initMenuItems(items: ContextMenuItem[]) {
        for (const item of items) {
            if (Configuration.isMac && item.shortcut)
                item.shortcut = item.shortcut.replace("Ctrl", "⌘");
        }

        this.setState(() => ({
            menuItems: items,
            keyMenuItems: this.buildKeyMenus(items),
        }));
    }
    addMenuItems(items: ContextMenuItem[]) {
        const additionals = ContextMenuContextContainer.additionalMenuItems;
        Logger.info(`Processing adding menu items, currently: ${additionals.length} additional items, adding ${items.length} items`);
        for (const item of items) {
            if (Configuration.isMac && item.shortcut)
                item.shortcut = item.shortcut.replace("Ctrl", "⌘");

            const existing = additionals.find(menu => menu.id === item.id);

            if (!!existing) additionals.replace(existing, item);
            else additionals.push(item);
        }

        Logger.info(`Processing complete, new additional count ${additionals.length} items`);
        this.setState(() => ({
            additionalMenuItems: additionals,
            keyMenuItems: this.buildKeyMenus(
                this.state.menuItems.concat(additionals),
            ),
        }));
    }

    hasTextSelection() {
        const selection = window.getSelection();
        return selection && selection.type === "Range";
    }
    isEditable() {
        if (
            !this.state.contextTarget ||
            this.state.contextTarget.nodeType !== 1
        )
            return false;

        return (
            this.state.contextTarget instanceof HTMLTextAreaElement ||
            (this.state.contextTarget instanceof HTMLInputElement &&
                /^(?:text|email|number|search|tel|url|password)$/i.test(
                    (this.state.contextTarget as HTMLInputElement).type,
                ))
        );
    }

    render() {
        this.context = {
            menuItems: this.state.menuItems,
            additionalMenuItems: this.state.additionalMenuItems,
            addMenuItems: this.addMenuItems.bind(this),
            setContext: this.setContext.bind(this),
            refreshRoute: this.props.refreshRoute,
            getKey: () => this.state.contextKey,
            getValue: () => this.state.contextValue,
            hasTextSelection: this.hasTextSelection.bind(this),
            isEditable: this.isEditable.bind(this),
            getContextTarget: () => this.state.contextTarget,
            getContextSelection: () => this.state.contextSelection,
        };

        return (
            <ContextMenuContext.Provider value={this.context}>
                {this.props.children}
            </ContextMenuContext.Provider>
        );
    }
}

export default ContextMenuContextContainer;
